// ============================================================================
// The facts about a shipment that several parts of this page have to agree on:
// has it landed, which half of the operation is it in, and is it running on the
// same-day express clock — plus the two IST readers those answers are built from.
//
// Their own module because the SLA rule, the Summary tiles and the escalation
// excusal all need them, and putting them in any one of the three would make that
// pair import each other.
// ============================================================================
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { LogisticsOrderRow } from "../types";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST = "Asia/Kolkata";

// The IST calendar day a value falls on. Handles BOTH shapes the backend sends: `delivery_date` is
// a bare date ("2026-09-19"), which is already an IST calendar date and must NOT be shifted through
// UTC (that lands it at 5:30 AM and, for a late-evening cutoff, on the wrong day); dispatch times
// are UTC instants and must be converted.
export const istDay = (v: string | null | undefined): Dayjs | null => {
  if (!v) return null;
  const bareDate = /^\d{4}-\d{2}-\d{2}$/.test(v);
  const d = bareDate ? dayjs.tz(v, IST) : dayjs.utc(v).tz(IST);
  return d.isValid() ? d.startOf("day") : null;
};

export const istInstant = (v: string | null | undefined): Dayjs | null => {
  if (!v) return null;
  const d = dayjs.utc(v);
  return d.isValid() ? d.tz(IST) : null;
};

export const isDelivered = (r: LogisticsOrderRow) => r.delivery_status === "delivered";

// Every booking carries a lane and the backend falls anything unrecognised to last mile, so the two
// are exhaustive — "not first" IS last, which is what keeps the mile split summing to its total.
export const isFirstMile = (r: LogisticsOrderRow) => r.board_lane === "first_mile";

// An express order placed ON its target delivery date — the one shipment whose promise is a ROLLING
// window (5 hours from the order) rather than a fixed hour of the day. Both the SLA cutoff and the
// escalation notice window hang off that distinction, and a fixed afternoon hour applied to one of
// these can land before the order was even placed, so the two must read it the same way.
export const isSameDayExpress = (r: LogisticsOrderRow): boolean => {
  if (!r.is_express || isFirstMile(r)) return false;
  const placedAt = istInstant(r.order_placed_time);
  const target = istDay(r.delivery_date);
  return !!placedAt && !!target && placedAt.isSame(target, "day");
};
